/* eslint-disable max-lines */
import {
  AnticipatedCareLevel,
  CitizenshipStatus,
  DependentDto,
  DependentSubtypeEnum,
  DependentTypeEnum,
  EmployeeDto,
  EnrollmentStatus,
  Gender,
  GenderEnum,
  InsuredPersonDto,
  InsuredSubtype,
  PossibleFipsCode,
  UpsertDependentDto,
} from '@zorro/clients';
import {
  ERROR_MESSAGES,
  VALIDATION_MESSAGES,
  convertFromMaybeYesNo,
  convertToMaybeYesNoEnum,
  convertToYesNoEnum,
  isValidSsn,
  parseEnumValue,
} from '@zorro/common-utils';
import {
  DateUtilInstance,
  earliestAllowedNonDependentBirthDate,
  formatDateISO,
  latestAllowedNonDependentBirthDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import { YesNo } from '@zorro/types';
import { validateAddress } from '@zorro/web/ui-utils';
import { Dispatch, SetStateAction } from 'react';
import { UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { InferType } from 'yup';

import {
  DependentSchema,
  InsuredDependentFormFields,
  InsuredDependentsFormFields,
} from './DependentsFormInputs';
import { InsuredEmployeeFormFields } from './EmployeeFormInputs';
import { InsuredSpouseFormFields } from './SpouseFormInputs';

export type SpouseStatus = {
  spouseWasRemoved: boolean;
  isSpouseActive: boolean;
};

/**
 * Validity of each sub-form, read once per render by the owning hook.
 *
 * react-hook-form only keeps `formState.isValid` up to date while something is
 * subscribed to it. A section that mounts later - the spouse accordion, which
 * only renders once the spouse loads - subscribes after react-hook-form has
 * already marked the form as mounted, so it never revalidates and `isValid`
 * stays stuck at its initial `false`. Reading all three in the hook body keeps
 * every sub-form subscribed from the first render.
 */
export type InsuredFormValidity = {
  isEmployeeValid: boolean;
  isSpouseValid: boolean;
  areDependentsValid: boolean;
};

export type UseInsuredFormReturnType = {
  employeeForm: UseFormReturn<InsuredEmployeeFormFields>;
  spouseForm: UseFormReturn<InsuredSpouseFormFields>;
  dependentsForm: UseFormReturn<InsuredDependentsFormFields>;
  formValidity: InsuredFormValidity;
  hasAttemptedSubmit: boolean;
  dependentSchema: DependentSchema;
  targetEnrollmentDate?: DateUtilInstance; // effectiveFrom from onboarding period
  onSubmit: () => Promise<boolean>;
  hasAnyDependents: boolean;
  // Employee's residential zip code, used to resolve county names by FIPS code
  employeeZipCode?: string | null;
  // The county resolved on the last save, for read-only display
  employeeFipsCode?: string | null;
  possibleFipsCodes: PossibleFipsCode[];
  setPossibleFipsCodes: (fipsCodes: PossibleFipsCode[]) => void;
  spousePossibleFipsCodes: PossibleFipsCode[];
  setSpousePossibleFipsCodes: (fipsCodes: PossibleFipsCode[]) => void;
  dependentPossibleFipsCodes: Record<number, PossibleFipsCode[]>;
  isDomesticPartnerAllowed: boolean;
  setDependentPossibleFipsCodes: (
    index: number,
    fipsCodes: PossibleFipsCode[]
  ) => void;
  spouseStatus: SpouseStatus;
  setSpouseStatus: Dispatch<SetStateAction<SpouseStatus>>;
  cancelSpouse: () => void;
  openedTabs: string[];
  setOpenedTabs: Dispatch<SetStateAction<string[]>>;
  withResidentialAddress?: boolean;
};

export const INSURED_FORM_ADDRESS_TOOLTIP_LABEL =
  'Shopping for individual health plans requires all household members to share the same residential address. If you have dependents at a different address, please contact an enrollment expert for assistance';

export const getInsuredBaseSchema = (
  isFinalizationMode: boolean,
  isEmployee: boolean,
  withAnticipatedCareLevel: boolean
) =>
  yup.object({
    firstName: yup
      .string()
      .required(VALIDATION_MESSAGES.firstNameRequired)
      .min(2, VALIDATION_MESSAGES.firstNameMinTwoLetters),
    lastName: yup
      .string()
      .required(VALIDATION_MESSAGES.lastNameRequired)
      .min(2, VALIDATION_MESSAGES.lastNameMinTwoLetters),
    customClassCategory: yup
      .string()
      .max(70, 'Limit to 70 characters')
      .matches(
        /^[ -~]*$/u,
        'Custom class category must only contain visible characters and no new lines or tabs'
      )
      .optional(),
    dateOfBirth: yup
      .date()
      .min(
        latestAllowedNonDependentBirthDate(),
        VALIDATION_MESSAGES.nonDependentMaxAgeValid
      )
      .max(
        earliestAllowedNonDependentBirthDate(),
        VALIDATION_MESSAGES.nonDependentMinAgeValid
      )
      .typeError(VALIDATION_MESSAGES.dateOfBirthRequired)
      .required(VALIDATION_MESSAGES.dateOfBirthRequired),
    anticipatedCareLevel:
      isFinalizationMode && withAnticipatedCareLevel
        ? yup
            .mixed<AnticipatedCareLevel>()
            .oneOf(
              Object.values(AnticipatedCareLevel),
              VALIDATION_MESSAGES.anticipatedCareLevelRequired
            )
            .required(VALIDATION_MESSAGES.anticipatedCareLevelRequired)
        : yup
            .mixed<AnticipatedCareLevel>()
            .oneOf(
              Object.values(AnticipatedCareLevel),
              VALIDATION_MESSAGES.anticipatedCareLevelRequired
            )
            .nullable(),
    ...(isFinalizationMode
      ? {
          residentialAddress: yup
            .string()
            .required(VALIDATION_MESSAGES.residentialAddressRequired),
          ssn: yup
            .string()
            .required(VALIDATION_MESSAGES.ssnRequired)
            .test('valid-ssn', VALIDATION_MESSAGES.ssnNumberValid, (value) =>
              value ? isValidSsn(value) : true
            ),
          citizenshipStatus: yup
            .mixed<CitizenshipStatus>()
            .oneOf(
              Object.values(CitizenshipStatus),
              VALIDATION_MESSAGES.citizenshipStatus
            )
            .typeError(VALIDATION_MESSAGES.citizenshipStatus)
            .required(VALIDATION_MESSAGES.citizenshipStatus),
          gender: yup
            .mixed<Gender>()
            .oneOf(Object.values(Gender), VALIDATION_MESSAGES.genderRequired)
            .required(VALIDATION_MESSAGES.genderRequired),
          isSmoker: yup
            .mixed<YesNo>()
            .oneOf(Object.values(YesNo), VALIDATION_MESSAGES.smokerRequired)
            .required(VALIDATION_MESSAGES.smokerRequired),
          isPregnant: yup.mixed<YesNo>().when('gender', {
            is: Gender.FEMALE,
            then: (schema) =>
              schema
                .oneOf(
                  Object.values(YesNo),
                  VALIDATION_MESSAGES.pregnantRequired
                )
                .required(VALIDATION_MESSAGES.pregnantRequired),
            otherwise: (schema) => schema.notRequired(),
          }),
        }
      : {
          residentialAddress: isEmployee
            ? yup
                .string()
                .required(VALIDATION_MESSAGES.residentialAddressRequired)
            : yup.string().optional(),
          ssn: isEmployee
            ? yup
                .string()
                .required(VALIDATION_MESSAGES.ssnRequired)
                .test(
                  'valid-ssn',
                  VALIDATION_MESSAGES.ssnNumberValid,
                  (value) => (value ? isValidSsn(value) : true)
                )
            : yup
                .string()
                .test(
                  'valid-ssn',
                  VALIDATION_MESSAGES.ssnNumberValid,
                  (value) => (value ? isValidSsn(value) : true)
                ),
          citizenshipStatus: yup
            .mixed<CitizenshipStatus>()
            .oneOf(
              Object.values(CitizenshipStatus),
              VALIDATION_MESSAGES.citizenshipStatus
            )
            .nullable(),
          gender: yup
            .mixed<Gender>()
            .oneOf(Object.values(Gender), VALIDATION_MESSAGES.genderRequired)
            .nullable(),
          isSmoker: yup
            .mixed<YesNo>()
            .oneOf(Object.values(YesNo), VALIDATION_MESSAGES.smokerRequired)
            .nullable(),
          isPregnant: yup
            .mixed<YesNo>()
            .oneOf(Object.values(YesNo), VALIDATION_MESSAGES.pregnantRequired)
            .nullable(),
        }),
  });

type BaseInsuredFormFields = InferType<ReturnType<typeof getInsuredBaseSchema>>;

// Shopping collects the address in its own step, after this form, so an empty
// one must not block it. Only requiredness changes, hence the cast.
export const relaxResidentialAddress = <TSchema extends yup.AnyObjectSchema>(
  schema: TSchema,
  withResidentialAddress: boolean
): TSchema =>
  withResidentialAddress
    ? schema
    : (schema.shape({
        residentialAddress: yup.string().optional(),
      }) as unknown as TSchema);

export const getDefaultInsuredFormFields = (
  residentialAddress?: InsuredPersonDto['residentialAddress']
): Omit<BaseInsuredFormFields, 'dateOfBirth'> => {
  return {
    residentialAddress: residentialAddress ?? '',
    firstName: '',
    lastName: '',
    gender: null,
    ssn: '',
    citizenshipStatus: null,
    isPregnant: YesNo.NO,
    isSmoker: YesNo.NO,
    customClassCategory: '',
  };
};

const convertGenderFormToDto = (
  gender: Gender | null | undefined
): GenderEnum | undefined => {
  return gender ? parseEnumValue(GenderEnum, gender) : undefined;
};

const convertGenderDtoToForm = (
  gender: GenderEnum | null | undefined
): Gender | undefined => {
  return gender ? parseEnumValue(Gender, gender) : undefined;
};

const convertDependentTypeFormToDto = (
  insuredSubtype: InsuredSubtype | null | undefined
): DependentSubtypeEnum | null => {
  return parseEnumValue(DependentSubtypeEnum, insuredSubtype || '') || null;
};

export const convertDependentSubtypeDtoToForm = (
  dependentSubtypeEnum: DependentSubtypeEnum | null | undefined
): InsuredSubtype | null => {
  return parseEnumValue(InsuredSubtype, dependentSubtypeEnum || '') || null;
};

export const mapDependentDtoToBaseInsuredFormFields = (
  dependentDto: DependentDto
): BaseInsuredFormFields => {
  return {
    firstName: dependentDto.firstName,
    lastName: dependentDto.lastName,
    dateOfBirth: parseDateISO(dependentDto.dateOfBirth).toDate(),
    gender: convertGenderDtoToForm(dependentDto.gender),
    ssn: dependentDto.ssn ?? undefined,
    citizenshipStatus: dependentDto.citizenshipStatus ?? undefined,
    residentialAddress: dependentDto.residentialAddress ?? undefined,
    isPregnant: convertToMaybeYesNoEnum(dependentDto.isPregnant),
    isSmoker: convertToMaybeYesNoEnum(dependentDto.isSmoker),
  };
};

export const mapFormFieldsToDependentDto = (
  formFields: InsuredSpouseFormFields,
  type: DependentTypeEnum,
  id?: string
): UpsertDependentDto => {
  return {
    id,
    firstName: formFields.firstName,
    lastName: formFields.lastName,
    gender: convertGenderFormToDto(formFields.gender),
    dateOfBirth: formatDateISO(formFields.dateOfBirth),
    isPregnant: convertFromMaybeYesNo(formFields.isPregnant),
    isSmoker: convertFromMaybeYesNo(formFields.isSmoker),
    isDisabled: Boolean(formFields.isDisabled),
    ssn: formFields.ssn,
    residentialAddress: formFields.residentialAddress || '',
    fipsCode: formFields.fipsCode || undefined,
    citizenshipStatus: formFields.citizenshipStatus || undefined,
    type,
    subType: convertDependentTypeFormToDto(formFields.subtype),
  };
};

export const mapInsuredDtoToBaseInsuredFormFields = (
  withAnticipatedCareLevel: boolean = false,
  insuredDto?: InsuredPersonDto | EmployeeDto
): BaseInsuredFormFields => {
  if (!insuredDto) {
    return {
      firstName: '',
      lastName: '',
      customClassCategory: '',
      dateOfBirth: parseDateISO('1970-01-01').toDate(),
      gender: Gender.MALE,
      ssn: '',
      residentialAddress: '',
      citizenshipStatus: null,
      isPregnant: YesNo.NO,
      isSmoker: YesNo.NO,
      anticipatedCareLevel: null,
    };
  }

  return {
    firstName: insuredDto.firstName,
    lastName: insuredDto.lastName,
    customClassCategory: insuredDto.customClassCategory || '',
    dateOfBirth: parseDateISO(insuredDto.dateOfBirth).toDate(),
    gender: insuredDto?.gender || Gender.MALE,
    ssn: ('ssn' in insuredDto && insuredDto?.ssn) || '',
    residentialAddress:
      ('residentialAddress' in insuredDto && insuredDto?.residentialAddress) ||
      '',
    citizenshipStatus:
      ('citizenshipStatus' in insuredDto && insuredDto.citizenshipStatus) ||
      null,
    isPregnant:
      'isPregnant' in insuredDto && insuredDto?.isPregnant
        ? convertToYesNoEnum(insuredDto.isPregnant)
        : YesNo.NO,
    isSmoker:
      'isSmoker' in insuredDto && insuredDto?.isSmoker
        ? convertToYesNoEnum(insuredDto.isSmoker)
        : YesNo.NO,
    anticipatedCareLevel:
      withAnticipatedCareLevel &&
      'anticipatedCareLevel' in insuredDto &&
      insuredDto?.anticipatedCareLevel
        ? parseEnumValue(AnticipatedCareLevel, insuredDto.anticipatedCareLevel)
        : undefined,
  };
};

export const shouldShowReadonlyMode = (enrollmentStatus: EnrollmentStatus) => {
  return [
    EnrollmentStatus.ENROLLMENT_IN_PROCESS,
    EnrollmentStatus.ENROLLMENT_CONFIRMED,
    EnrollmentStatus.ELECTION_SUBMITTED,
    EnrollmentStatus.ACTIVE_COVERAGE,
    EnrollmentStatus.COVERAGE_ENDED,
  ].includes(enrollmentStatus);
};

type ValidatedAddress = { fipsCode: string; formattedAddress?: string };

type StoredAddress = {
  residentialAddress?: string | null;
  fipsCode?: string | null;
};

type StoredDependentAddress = StoredAddress & { id: string };

/**
 * The rows the form was loaded with, one per person, so that each member is
 * compared only against their own stored address.
 */
export type StoredHouseholdAddresses = {
  employee?: StoredAddress | null;
  spouse?: StoredAddress | null;
  dependents?: StoredDependentAddress[] | null;
};

/**
 * A row only carries a `fipsCode` once a resolution completed, which is the
 * same condition the server uses to reuse a stored resolution.
 */
export const isUnchangedResolvedAddress = (
  stored: StoredAddress | null | undefined,
  residentialAddress: string | undefined
): boolean =>
  Boolean(
    stored?.fipsCode &&
    residentialAddress &&
    stored.residentialAddress === residentialAddress
  );

export const getStoredHouseholdFromEmployee = (
  employee: EmployeeDto | undefined,
  spouse: DependentDto | undefined,
  dependents: DependentDto[] | undefined
): StoredHouseholdAddresses => ({
  employee: employee && {
    residentialAddress: employee.address,
    fipsCode: employee.fipsCode,
  },
  spouse,
  dependents,
});

const tryResolveFromDisambiguated = (
  address: string | undefined,
  fipsCode: string | undefined,
  disambiguatedAddresses: Map<string, ValidatedAddress> | undefined,
  setFipsCode: ((code: string) => void) | undefined,
  setResidentialAddress: ((address: string) => void) | undefined
): boolean | null => {
  if (!address || !disambiguatedAddresses?.has(address)) {
    return null;
  }
  const known = disambiguatedAddresses.get(address);
  if (known?.fipsCode && !fipsCode) {
    setFipsCode?.(known.fipsCode);
  }
  applyFormattedAddress(
    address,
    known?.formattedAddress,
    setResidentialAddress
  );
  return true;
};

/**
 * Replaces what the user typed with the provider's canonical form of the same
 * address, so that the value submitted matches the one already stored. Without
 * this the server compares a raw string against a formatted one and treats an
 * unchanged address as a move.
 */
const applyFormattedAddress = (
  address: string,
  formattedAddress: string | undefined,
  setResidentialAddress: ((address: string) => void) | undefined
): void => {
  if (formattedAddress && formattedAddress !== address) {
    setResidentialAddress?.(formattedAddress);
  }
};

export const validateInsuredAddress = async (
  residentialAddress: string | undefined,
  fipsCode: string | undefined,
  setFormError: (
    field: 'residentialAddress' | 'fipsCode',
    error: { message: string }
  ) => void,
  setFipsCodes: (codes: PossibleFipsCode[]) => void,
  stopLoading: () => void,
  disambiguatedAddresses?: Map<string, ValidatedAddress>,
  setFipsCode?: (code: string) => void,
  storedAddress?: StoredAddress | null,
  setResidentialAddress?: (address: string) => void
): Promise<boolean> => {
  // This person's unchanged address was resolved when it was stored, and the
  // server reuses that resolution rather than re-deriving it, so checking it
  // again buys nothing. The county is deliberately left alone: seeding the
  // stored one onto the form would tell the server the user picked it and stop
  // it ever being corrected.
  if (isUnchangedResolvedAddress(storedAddress, residentialAddress)) {
    return true;
  }

  const cached = tryResolveFromDisambiguated(
    residentialAddress,
    fipsCode,
    disambiguatedAddresses,
    setFipsCode,
    setResidentialAddress
  );
  if (cached !== null) {
    return cached;
  }

  const { isValidAddress, isPoBox, possibleFipsCodes, formattedAddress } =
    await validateAddress(residentialAddress);

  if (isPoBox || !isValidAddress) {
    setFormError('residentialAddress', {
      message: isPoBox
        ? ERROR_MESSAGES.ADDRESS_IS_PO_BOX_ERROR_MESSAGE
        : VALIDATION_MESSAGES.residentialAddressInvalid,
    });
    stopLoading();
    return false;
  }

  if (possibleFipsCodes && possibleFipsCodes.length > 1 && !fipsCode) {
    if (residentialAddress) {
      disambiguatedAddresses?.set(residentialAddress, {
        fipsCode: '',
        formattedAddress,
      });
    }
    setFipsCodes(possibleFipsCodes);
    setFormError('fipsCode', {
      message: VALIDATION_MESSAGES.countyDisambiguity,
    });
    stopLoading();
    return false;
  }

  if (residentialAddress) {
    disambiguatedAddresses?.set(residentialAddress, {
      fipsCode: fipsCode ?? '',
      formattedAddress,
    });
    applyFormattedAddress(
      residentialAddress,
      formattedAddress,
      setResidentialAddress
    );
  }

  return true;
};

const findStoredDependent = (
  storedDependents: StoredDependentAddress[] | null | undefined,
  insuredId: string | undefined
): StoredAddress | undefined =>
  insuredId
    ? storedDependents?.find((stored) => stored.id === insuredId)
    : undefined;

export const validateDependentsAddresses = async (
  dependents: {
    residentialAddress?: string;
    fipsCode?: string;
    insuredId?: string;
  }[],
  setFormError: (
    index: number,
    field: 'residentialAddress' | 'fipsCode',
    error: { message: string }
  ) => void,
  setFipsCodes: (index: number, codes: PossibleFipsCode[]) => void,
  stopLoading: () => void,
  disambiguatedAddresses?: Map<string, ValidatedAddress>,
  setDependentFipsCode?: (index: number, code: string) => void,
  storedDependents?: StoredDependentAddress[] | null,
  setDependentResidentialAddress?: (index: number, address: string) => void
): Promise<number[]> => {
  const invalidIndices: number[] = [];
  for (const [index, dependent] of dependents.entries()) {
    if (dependent.residentialAddress) {
      const isValid = await validateInsuredAddress(
        dependent.residentialAddress,
        dependent.fipsCode,
        (field, error) => setFormError(index, field, error),
        (codes) => setFipsCodes(index, codes),
        stopLoading,
        disambiguatedAddresses,
        setDependentFipsCode
          ? (code) => setDependentFipsCode(index, code)
          : undefined,
        findStoredDependent(storedDependents, dependent.insuredId),
        setDependentResidentialAddress
          ? (address) => setDependentResidentialAddress(index, address)
          : undefined
      );
      if (!isValid) {
        invalidIndices.push(index);
      }
    }
  }
  return invalidIndices;
};

type AddressError = { message: string };
type AddressFieldError = (
  field: 'residentialAddress' | 'fipsCode',
  error: AddressError
) => void;

export const validateAllAddressesAndGetFailedTabs = async (
  employee: { residentialAddress?: string },
  fipsCode: string | undefined,
  onEmployeeError: AddressFieldError,
  onSetEmployeeFipsCode: (code: string) => void,
  setEmployeeFipsCodes: (codes: PossibleFipsCode[]) => void,
  spouse: InsuredSpouseFormFields,
  spouseStatus: SpouseStatus,
  onSpouseError: AddressFieldError,
  onSetSpouseFipsCode: (code: string) => void,
  setSpouseFipsCodes: (codes: PossibleFipsCode[]) => void,
  dependents: InsuredDependentsFormFields['dependents'],
  onDependentError: (
    index: number,
    field: 'residentialAddress' | 'fipsCode',
    error: AddressError
  ) => void,
  onSetDependentFipsCode: (index: number, code: string) => void,
  setDependentFipsCodes: (index: number, codes: PossibleFipsCode[]) => void,
  conditionalStopLoading: () => void,
  stored: StoredHouseholdAddresses = {},
  onSetEmployeeResidentialAddress?: (address: string) => void,
  onSetSpouseResidentialAddress?: (address: string) => void,
  onSetDependentResidentialAddress?: (index: number, address: string) => void
): Promise<string[]> => {
  const disambiguatedAddresses = new Map<string, ValidatedAddress>();
  const failedTabs: string[] = [];

  const isEmployeeAddressValid = await validateInsuredAddress(
    employee.residentialAddress,
    fipsCode,
    onEmployeeError,
    setEmployeeFipsCodes,
    conditionalStopLoading,
    disambiguatedAddresses,
    onSetEmployeeFipsCode,
    stored.employee,
    onSetEmployeeResidentialAddress
  );
  if (!isEmployeeAddressValid) {
    failedTabs.push('EMPLOYEE');
  }

  if (spouseStatus.isSpouseActive && spouse.residentialAddress) {
    const isSpouseAddressValid = await validateInsuredAddress(
      spouse.residentialAddress,
      spouse.fipsCode,
      onSpouseError,
      setSpouseFipsCodes,
      conditionalStopLoading,
      disambiguatedAddresses,
      onSetSpouseFipsCode,
      spouseStatus.spouseWasRemoved ? undefined : stored.spouse,
      onSetSpouseResidentialAddress
    );
    if (!isSpouseAddressValid) {
      failedTabs.push('SPOUSE');
    }
  }

  const invalidDepIndices = await validateDependentsAddresses(
    dependents,
    onDependentError,
    setDependentFipsCodes,
    conditionalStopLoading,
    disambiguatedAddresses,
    onSetDependentFipsCode,
    stored.dependents,
    onSetDependentResidentialAddress
  );
  for (const depIndex of invalidDepIndices) {
    failedTabs.push(`DEPENDENT-${depIndex}`);
  }

  return failedTabs;
};

export const hasMissingRequiredFields = ({
  hasAttemptedSubmit,
  formValidity,
  spouseStatus,
  hasAnyDependents,
}: Pick<
  UseInsuredFormReturnType,
  'hasAttemptedSubmit' | 'formValidity' | 'spouseStatus' | 'hasAnyDependents'
>): boolean =>
  hasAttemptedSubmit &&
  !(
    formValidity.isEmployeeValid &&
    (!spouseStatus.isSpouseActive || formValidity.isSpouseValid) &&
    (!hasAnyDependents || formValidity.areDependentsValid)
  );

export const getInvalidTabs = (
  isEmployeeValid: boolean,
  isSpouseActive: boolean,
  isSpouseValid: boolean,
  hasAnyDependents: boolean,
  dependentCount: number,
  hasDependentError: (index: number) => boolean
): string[] => {
  const tabs: string[] = [];
  if (!isEmployeeValid) tabs.push('EMPLOYEE');
  if (isSpouseActive && !isSpouseValid) tabs.push('SPOUSE');
  if (hasAnyDependents) {
    for (let index = 0; index < dependentCount; index++) {
      if (hasDependentError(index)) tabs.push(`DEPENDENT-${index}`);
    }
  }
  return tabs;
};

export const getInsuredFormIsDirty = ({
  employeeForm,
  spouseStatus,
  spouseForm,
  hasAnyDependents,
  dependentsForm,
}: UseInsuredFormReturnType) => {
  return (
    employeeForm.formState.isDirty ||
    (spouseStatus.isSpouseActive && spouseForm.formState.isDirty) ||
    (hasAnyDependents && dependentsForm.formState.isDirty)
  );
};

export const getIsDependentValid = (
  dependentSchema: DependentSchema,
  dependent: InsuredDependentFormFields | undefined
) => Boolean(dependent) && dependentSchema.isValidSync(dependent);
